/**
 * ZTickets - Zod Validation Schemas for the internal Tickets API
 *
 * The internal tickets surface (`/api/tickets`) is served by
 * InternalTicketsController and is org-scoped (Clerk JWT). It reuses the same
 * ticket schemas as the public v1 API — the shapes are identical, so the
 * schemas are the single source of truth for both surfaces.
 *
 * Note: distinct from the public v1 tickets routes (`/api/public/v1/...`).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  CreateTicketBodySchema,
  CreateTicketCommentBodySchema,
  ListTicketsParamsSchema,
  TicketCommentSchema,
  TicketDetailSchema,
  TicketIdParamSchema,
  TicketSummarySchema,
  UpdateTicketBodySchema
} from "../../public/v1/schemas/ticket.schemas";

// ═══════════════════════════════════════════════════════════════
// RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** List returns the ticket array as `data`, with pagination in `meta`. */
export const ListTicketsDataSchema = z.array(TicketSummarySchema);

/** Comments are wrapped in a `{ comments }` object to match the controller. */
export const ListTicketCommentsDataSchema = z.object({
  comments: z.array(TicketCommentSchema)
});

// ═══════════════════════════════════════════════════════════════
// ZTICKETS MANIFEST
// ═══════════════════════════════════════════════════════════════

/**
 * ZTickets - Zod API validation schemas for the internal tickets surface.
 *
 * `List` is generated with a raw variant so consumers can read pagination
 * metadata (`meta.total`) from the envelope; the other endpoints auto-unwrap.
 */
export const ZTickets = {
  /**
   * Create a ticket.
   * POST /api/tickets
   */
  Create: {
    method: "POST" as const,
    path: "/api/tickets",
    Body: CreateTicketBodySchema,
    Response: ApiSuccessSchema(TicketDetailSchema)
  },

  /**
   * List tickets (paginated + filterable). Pagination lives in `meta`.
   * GET /api/tickets
   */
  List: {
    method: "GET" as const,
    path: "/api/tickets",
    Query: ListTicketsParamsSchema,
    Response: ApiSuccessSchema(ListTicketsDataSchema)
  },

  /**
   * Get a single ticket by id.
   * GET /api/tickets/:ticketId
   */
  Get: {
    method: "GET" as const,
    path: "/api/tickets/:ticketId",
    PathVars: TicketIdParamSchema,
    Response: ApiSuccessSchema(TicketDetailSchema)
  },

  /**
   * Update a ticket.
   * PATCH /api/tickets/:ticketId
   */
  Update: {
    method: "PATCH" as const,
    path: "/api/tickets/:ticketId",
    PathVars: TicketIdParamSchema,
    Body: UpdateTicketBodySchema,
    Response: ApiSuccessSchema(TicketDetailSchema)
  },

  /**
   * Add a comment to a ticket.
   * POST /api/tickets/:ticketId/comments
   */
  AddComment: {
    method: "POST" as const,
    path: "/api/tickets/:ticketId/comments",
    PathVars: TicketIdParamSchema,
    Body: CreateTicketCommentBodySchema,
    Response: ApiSuccessSchema(TicketCommentSchema)
  },

  /**
   * List a ticket's comments.
   * GET /api/tickets/:ticketId/comments
   */
  ListComments: {
    method: "GET" as const,
    path: "/api/tickets/:ticketId/comments",
    PathVars: TicketIdParamSchema,
    Response: ApiSuccessSchema(ListTicketCommentsDataSchema)
  }
} as const;
